import { ChangeEvent, KeyboardEvent, RefObject, SyntheticEvent, useEffect, useRef, useState, useCallback } from "react"
import { Button, Modal, Form, Table, InputGroup, Card, Container, Row, Col, Alert, DropdownButton, ButtonGroup, Dropdown, Spinner } from "react-bootstrap"
import ContentBody from "../../components/ContentBody"
import ContentHeader from "../../components/ContentHeader"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import Pagination from "../../components/Pagination"
import Configs from "../../Configs"
import VoucherService, { AdjustOrReplaceInvoiceInput, IInvoice, IssueInvoicesInput, IVoucher, IVoucherUpdate, VoucherSortFields } from "../../services/VoucherService"
import useAppContext from "../../hooks/useAppContext"
import VoucherTypeSelect from "../../components/VoucherTypeSelect"
import Utils from "../../utils/Utils"
import HtmlToPrint from "../../components/HtmlToPrint"
import { useReactToPrint } from "react-to-print"
import TooltipInfo from "../../components/TooltipInfo"
import TicketTemplateService, { ITicketTemplate } from "../../services/TicketTemplateService"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"
import SortBy from "../../components/SortBy"
import { useTranslation } from "react-i18next"
import { InvoiceStatusType } from "../../constants/InvoiceStatusType"
import Confirmation from "../../components/Confirmation"
import DatePicker from "../../components/DatePicker"
import PlaceSelect from "../../components/PlaceSelect"

function Vouchers() {
    const { t } = useTranslation("global")
    const { hasPermission } = useAuthContext()
    const { state } = useAppContext()

    const [tableData, setTableData] = useState({
        data: new Array<IVoucher>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const initFormData: IVoucherUpdate = {
        id: null,
        code: "",
        placeId: null,
        voucherTypeId: null,
        paymentService: "",
        note: "",
        quantity: 1,
        isSameCode: false,
        isTest: null
    }

    const initAdjustOrReplaceInvoiceInput: AdjustOrReplaceInvoiceInput = {
        referenceType: null,
        orgInvoiceType: null,
        invoiceNote: ""
    }

    const [message, setMessage] = useState("")
    const [messageFormModal, setMessageFormModal] = useState("")
    const [validatedFormModal, setValidatedFormModal] = useState(false)
    const [formData, setFormData] = useState(initFormData)
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [sortBy, setSortBy] = useState("createdOn")
    const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC")
    const [adjustOrReplaceInvoiceInput, setAdjustOrReplaceInvoiceInput] = useState(initAdjustOrReplaceInvoiceInput)
    const [validatedAdjustOrReplaceInvoiceInput, setValidatedAdjustOrReplaceInvoiceInput] = useState(false)
    const [selectedItem, setSelectedItem] = useState({
        id: 0,
        name: ""
    })

    const keywordRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const invoiceFormRef = useRef<HTMLFormElement>(null)

    const FilterFn = async (pageIndex: number) => {
        if (state.currentProviderId) {
            let pageSize = Configs.DEFAULT_PAGE_SIZE
            let keyword = keywordRef.current?.value || ""

            const res = await VoucherService.filter(state.currentProviderId, pageIndex, pageSize, keyword, sortBy, sortDirection, Configs.Voucher.getHideTestVouchers())

            if (res?.isSuccess) {
                setTableData(res.data)
            } else {
                console.log(res?.message)
            }

            setCurrentPageIndex(pageIndex)
        }
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)
    }, [state.currentProviderId, sortBy, sortDirection])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    const handleFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        var name = e.target.name
        var value = e.target.value

        if (name === "placeId") {
            let values = {
                placeId: value ? parseInt(value) : null,
                voucherTypeId: null
            }
            setFormData({ ...formData, ...values })
        } else if (name === "quantity" || name === "voucherTypeId") {
            setFormData({ ...formData, [name]: value ? parseInt(value) : null })
        } else if (name === "paymentService") {
            if (e.target.checked) {
                setFormData({ ...formData, [e.target.name]: "Free" })
            } else {
                setFormData({ ...formData, [e.target.name]: "" })
            }
        } else if (name === "isSameCode") {
            setFormData({ ...formData, [name]: e.target.checked })
        } else if (name === "isTest") {
            setFormData({ ...formData, [name]: e.target.checked })
        } else {
            setFormData({ ...formData, [name]: value })
        }
    }

    const handleAddNew = () => {
        setFormData(initFormData)

        handleShowModal()
    }

    const handleEdit = async (id: number | null) => {
        if (state.currentProviderId && id) {
            const res = await VoucherService.get(state.currentProviderId, id)

            if (res?.isSuccess) {
                setFormData(res.data)

                handleShowModal()
            } else {
                console.log(res?.message)
            }
        }
    }

    const handleSave = async (e: SyntheticEvent) => {
        e.preventDefault()

        if (state.currentProviderId) {
            if (formRef.current?.checkValidity()) {
                if (formData.id) {
                    const res = await VoucherService.update(state.currentProviderId, formData.id, formData)

                    if (res?.isSuccess) {
                        FilterFn(currentPageIndex)
                        handleCloseModal()
                        setMessage(res.message)
                    } else {
                        setMessageFormModal(res?.message)
                    }
                } else {
                    const res = await VoucherService.create(state.currentProviderId, formData)

                    if (res?.isSuccess) {
                        FilterFn(1)
                        handleCloseModal()
                        setMessage(res.message)

                        //print
                        var ids = res.data.map((x: IVoucher) => x.id)
                        if (ids.length) {
                            if (Configs.PrintMethod.get() === "HTML") {
                                setVoucherIds(ids)
                            } else {
                                VoucherService.generatePDFs(state.currentProviderId, ids)
                            }
                        }
                    } else {
                        setMessageFormModal(res?.message)
                    }
                }
            } else {
                setValidatedFormModal(true)
            }
        }
    }

    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
        setValidatedFormModal(false)
        setMessageFormModal("")
    }
    const handleShowModal = () => {
        setShowModal(true)
        setMessage("")
    }

    const modalTitle = formData.id ? "Edit Voucher" : "Create New Voucher"

    //Delete
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setSelectedItem({
            id: 0,
            name: ""
        })

        setDisplayConfirmationModal(false)
    }

    const showDeleteConfirmation = (id: number | null, name: string) => {
        setSelectedItem({
            id: id || 0,
            name: name
        })

        setDisplayConfirmationModal(true)
        setMessage("")
    }

    const handleDelete = async () => {
        if (state.currentProviderId && selectedItem.id) {
            const res = await VoucherService.delete(state.currentProviderId, selectedItem.id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            }

            hideConfirmationModal()
            setMessage(res.message)
        }
    }

    //print
    const [isPrinting, setIsPrinting] = useState(false)
    const printRef = useRef(null)
    const onBeforeGetContentResolve = useRef<(() => void) | null>(null)
    const [voucherIds, setVoucherIds] = useState<number[]>([])
    const [voucherContentToPrint, setVoucherContentToPrint] = useState<ITicketTemplate | null>(null)

    const handlePrint = (id: number | null) => {
        if (state.currentProviderId && id) {
            if (Configs.PrintMethod.get() === "HTML") {
                setVoucherIds([id])
            } else {
                VoucherService.generatePDF(state.currentProviderId, id)
            }
        }
    }

    useEffect(() => {
        loadVoucherContent()
    }, [voucherIds])

    const loadVoucherContent = async () => {
        if (state.currentProviderId && voucherIds.length) {
            const template = await TicketTemplateService.getAppVoucherHTMLTemplate(state.currentProviderId)

            if (template?.isSuccess && template.data) {
                const ticketTemplate: ITicketTemplate = template.data

                const res = await VoucherService.getForPrint(state.currentProviderId, voucherIds)

                if (res?.isSuccess) {
                    var html = ""
                    res.data.forEach((voucher: IVoucher, index: number) => {
                        var contentTemplate = ticketTemplate.htmlContent
                        var logo = (ticketTemplate.logo || "") !== "" ? `${Configs.API_BASE_URL}${ticketTemplate.logo}` : ""
                        var bgImage = (ticketTemplate.bgImage || "") !== "" ? `${Configs.API_BASE_URL}${ticketTemplate.bgImage}` : ""

                        if (index > 0) {
                            html += '<div class="page-break" />'
                        }

                        html += contentTemplate
                            .replaceAll("{code}", voucher.code || "")
                            .replaceAll("{qrcode}", voucher.qrcodeImage)
                            .replaceAll("{price}", Utils.formatNumber(voucher.price))
                            .replaceAll("{currency}", voucher.currency)
                            .replaceAll("{timeLimit}", voucher.timeLimit?.toString() || "")
                            .replaceAll("{placeName}", voucher.placeName || "")
                            .replaceAll("{logo}", logo)
                            .replaceAll("{bgImage}", bgImage)
                            .replaceAll("{createdOn}", Utils.dateToString(voucher.createdOn, `${Configs.DATE_FORMAT} ${Configs.TIME_FORMAT}`))
                    })

                    setVoucherContentToPrint({ ...ticketTemplate, htmlContent: html })
                } else {
                    setVoucherContentToPrint(null)
                }
            } else {
                setVoucherContentToPrint(null)
            }
        } else {
            setVoucherContentToPrint(null)
        }
    }

    useEffect(() => {
        if (voucherContentToPrint) {
            handlePrintHTML()
        }
    }, [voucherContentToPrint])

    const handleOnBeforeGetContent = useCallback(() => {
        setIsPrinting(true)

        return new Promise<void>((resolve) => {
            onBeforeGetContentResolve.current = resolve
            setIsPrinting(false)
            setVoucherIds([])
            resolve()
        })
    }, [isPrinting, voucherContentToPrint])

    const handlePrintHTML = useReactToPrint({
        content: () => printRef?.current,
        onBeforeGetContent: handleOnBeforeGetContent,
        onAfterPrint: () => {
            onBeforeGetContentResolve.current = null
            setVoucherContentToPrint(null)
        },
        removeAfterPrint: true
    })

    useEffect(() => {
        if (voucherContentToPrint && typeof onBeforeGetContentResolve.current === "function") {
            onBeforeGetContentResolve.current()
        }
    }, [onBeforeGetContentResolve.current, voucherContentToPrint])

    //Issue Invoice
    const [processing, setProcessing] = useState<boolean>(false)
    const [displayIssueInvoiceConfirmationModal, setDisplayIssueInvoiceConfirmationModal] = useState(false)

    const hideIssueInvoiceConfirmationModal = () => {
        setSelectedItem({
            id: 0,
            name: ""
        })
        setDisplayIssueInvoiceConfirmationModal(false)
    }

    const showIssueInvoiceConfirmation = (id: number | null, name: string) => {
        setSelectedItem({
            id: id || 0,
            name: name
        })
        setDisplayIssueInvoiceConfirmationModal(true)
        setMessage("")
    }

    const handleIssueInvoice = async () => {
        if (!processing) {
            setProcessing(true)
            hideIssueInvoiceConfirmationModal()
            if (state.currentProviderId && selectedItem.id) {
                const res = await VoucherService.issueInvoice(state.currentProviderId, selectedItem.id)

                if (res?.isSuccess) {
                    FilterFn(currentPageIndex)
                }
                setMessage(res.message)
            }
            setProcessing(false)
        }
    }

    // AdjustOrReplaceInvoice
    const [displayAdjustOrReplaceInvoiceModal, setDisplayAdjustOrReplaceInvoiceModal] = useState(false)

    const hideAdjustOrReplaceInvoiceModal = () => {
        setSelectedItem({
            id: 0,
            name: ""
        })
        setDisplayAdjustOrReplaceInvoiceModal(false)
        setValidatedAdjustOrReplaceInvoiceInput(false)
        setAdjustOrReplaceInvoiceInput(initAdjustOrReplaceInvoiceInput)
        setMessageFormModal("")
    }

    const showAdjustOrReplaceInvoiceModal = (id: number | null, name: string) => {
        setSelectedItem({
            id: id || 0,
            name: name
        })
        setDisplayAdjustOrReplaceInvoiceModal(true)
        setMessage("")
    }

    const handleAdjustOrReplaceInvoice = async () => {
        if (!processing) {
            setProcessing(true)
            if (state.currentProviderId && selectedItem.id) {
                if (invoiceFormRef.current?.checkValidity()) {
                    hideAdjustOrReplaceInvoiceModal()
                    const res = await VoucherService.adjustOrReplaceInvoice(state.currentProviderId, selectedItem.id, adjustOrReplaceInvoiceInput)
                    if (res?.isSuccess) {
                        FilterFn(currentPageIndex)
                    }
                    setMessage(res.message)
                } else {
                    setValidatedAdjustOrReplaceInvoiceInput(true)
                }
            }
            setProcessing(false)
        }
    }

    const handleAdjustOrReplaceInvoiceFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        var name = e.target.name
        var value = e.target.value

        if (name === "referenceType" || name === "orgInvoiceType") {
            setAdjustOrReplaceInvoiceInput({ ...adjustOrReplaceInvoiceInput, [name]: value ? parseInt(value) : null })
        } else {
            setAdjustOrReplaceInvoiceInput({ ...adjustOrReplaceInvoiceInput, [name]: value })
        }
    }

    //Issue Invoices By Date
    const issueInvoicesFormRef = useRef<HTMLFormElement>(null)
    const [validatedIssueInvoicesFormModal, setValidatedIssueInvoicesFormModal] = useState(false)
    const [showIssueInvoicesModal, setShowIssueInvoicesModal] = useState(false)
    const [issueInvoicesInput, setIssueInvoicesInput] = useState<IssueInvoicesInput>({
        activedOn: Utils.getCurrentDate(),
        placeId: null
    })
    const [invoices, setInvoices] = useState<Array<IInvoice>>([])
    const [isSelectedAll, setIsSelectedAll] = useState(false)

    const hideIssueInvoicesModal = () => {
        setIssueInvoicesInput({
            activedOn: Utils.getCurrentDate(),
            placeId: null
        })
        setInvoices([])
        setShowIssueInvoicesModal(false)
        setIsSelectedAll(false)
        setMessageFormModal("")
    }

    const getInvoices = async (issueInvoicesInput: IssueInvoicesInput) => {
        if (state.currentProviderId) {
            const res = await VoucherService.getForIssueInvoices(state.currentProviderId, issueInvoicesInput)

            if (res?.isSuccess) {
                setInvoices(res.data)
            } else {
                setInvoices([])
                console.log(res?.message)
            }
        }else{
            setInvoices([])
        }
    }

    useEffect(() => {
        if (state.currentProviderId && showIssueInvoicesModal) {
            if (issueInvoicesInput.activedOn && issueInvoicesInput.placeId) {
                getInvoices(issueInvoicesInput)
            } else {
                setInvoices([])
            }
        }
    }, [issueInvoicesInput])

    const handleCheckAllInvoices = (e: ChangeEvent<HTMLInputElement>) => {
        setIsSelectedAll(e.target.checked)
        setInvoices(invoices.map(x => { return { ...x, isSelected: e.target.checked } }))
    }

    const handleCheckOneInvoice = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setIsSelectedAll(!invoices.some(x => x.id != parseInt(e.target.value) && !x.isSelected))
        } else {
            setIsSelectedAll(false)
        }
        setInvoices(invoices.map(x => { return { ...x, isSelected: x.id == parseInt(e.target.value) ? e.target.checked : x.isSelected } }))
    }

    const handleIssueInvoicesFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        var name = e.target.name
        var value = e.target.value

        if (name === "placeId") {
            setIssueInvoicesInput({ ...issueInvoicesInput, [name]: value ? parseInt(value) : null })
        } else {
            setIssueInvoicesInput({ ...issueInvoicesInput, [name]: value })
        }
    }

    const handleIssueInvoicesByDate = async (e: SyntheticEvent) => {
        e.preventDefault()
        if (!processing) {
            setProcessing(true)
            if (state.currentProviderId) {
                if (issueInvoicesFormRef.current?.checkValidity()) {
                    var voucherIds = invoices.filter(x => x.isSelected && x.id).map(x => x.id || 0)
                    const res = await VoucherService.issueInvoices(state.currentProviderId, issueInvoicesInput, voucherIds)

                    if (res?.isSuccess) {
                        getInvoices(issueInvoicesInput)
                        FilterFn(currentPageIndex)
                    }
                    setMessageFormModal(res.message)
                } else {
                    setValidatedIssueInvoicesFormModal(true)
                }
            }
            setProcessing(false)
        }
    }

    return (
        <>
            <ContentHeader title="All Vouchers" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.AllVouchers.Create) &&
                                        <Button variant="info" size="sm" onClick={handleAddNew}>
                                            <i className="fas fa-plus"></i> Create new voucher
                                        </Button>
                                    }
                                    {hasPermission(Permissions.AllVouchers.IssueInvoice) &&
                                        <Button variant="warning" size="sm" className="ml-1" onClick={() => setShowIssueInvoicesModal(true)}>
                                            <i className="fas fa-file-invoice"></i> Issue Invoices
                                        </Button>
                                    }

                                    <div className="card-tools" style={{ display: "flex" }}>
                                        <SortBy fields={VoucherSortFields} sortBy={sortBy} sortDirection={sortDirection} onFieldChange={name => setSortBy(name)} onDirectionChange={name => setSortDirection(name)}></SortBy>

                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table responsive striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Created On</th>
                                                <th>Code</th>
                                                <th>Place</th>
                                                <th>Price</th>
                                                <th>Note</th>
                                                <th style={{ textAlign: "center", width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.id}>
                                                        <td>
                                                            {Utils.dateToString(item.createdOn, Configs.DATE_TIME_FORMAT)}
                                                            <TooltipInfo title={item.userId}></TooltipInfo>
                                                            {(item.paymentService || item.isTest) && <div className="text-muted" style={{ fontSize: "11px" }}>
                                                                {item.paymentService || ""}
                                                                {item.isTest && <> (Test)</>}
                                                            </div>}
                                                            {item.creator?.userName && <div className="text-muted" style={{ fontSize: "11px" }}>
                                                                Created by: <span className="text-primary">{item.creator?.userName}</span>
                                                            </div>}
                                                        </td>
                                                        <td>
                                                            <h6>{item.code}</h6>
                                                            <div className="text-muted" style={{ fontSize: "11px" }}>
                                                                {item.secretKey}
                                                            </div>
                                                            <div className="text-muted" style={{ fontSize: "11px" }}>
                                                                Actived on: <span className="text-blue">{Utils.dateToString(item.activedOn, Configs.DATE_TIME_FORMAT)}</span>
                                                            </div>
                                                            <div className="text-muted" style={{ fontSize: "11px" }}>
                                                                Expired on: {Utils.dateToString(item.expiredOn, Configs.DATE_TIME_FORMAT)}
                                                            </div>
                                                        </td>
                                                        <td>{item.place?.name || ""}</td>
                                                        <td>
                                                            {item.price && <span className="text-danger">{Utils.formatNumber(item.price)} {item.currency}</span>}/{(item.timeLimit || 0) > 0 ? <span className="text-muted">{item.timeLimit} hours</span> : <span className="text-muted">Unlimited</span>}
                                                            {item.voucherType?.name && <div className="text-muted">
                                                                ({item.voucherType?.name || ""})
                                                            </div>}
                                                            {item.invoiceStatus && <div>
                                                                <span className="text-muted">{t("eventTicketsPage.invoiceStatus")}:</span> <span className={item.invoiceStatus === InvoiceStatusType.ISSUED ? "text-green" : "text-danger"}>{item.invoiceStatus && t(`invoiceStatus.${item.invoiceStatus}`)}</span>
                                                            </div>}
                                                        </td>
                                                        <td style={{ whiteSpace: "pre-wrap" }}>{item.note}</td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            <DropdownButton
                                                                as={ButtonGroup}
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                className="btn-actions-dropdown"
                                                                align={{ lg: 'end' }}
                                                                title={t("common.actions")}
                                                            >
                                                                <Dropdown.ItemText className="text-muted">VOUCHER</Dropdown.ItemText>
                                                                {hasPermission(Permissions.AllVouchers.Print) &&
                                                                    <Dropdown.Item onClick={() => handlePrint(item.id)}><i className="fas fa-print"></i> Print</Dropdown.Item>
                                                                }
                                                                {hasPermission(Permissions.AllVouchers.Edit) &&
                                                                    <Dropdown.Item onClick={() => handleEdit(item.id)}><i className="fas fa-pencil-alt text-warning"></i> Edit</Dropdown.Item>
                                                                }
                                                                {hasPermission(Permissions.AllVouchers.Delete) && <>
                                                                    <Dropdown.Item onClick={() => showDeleteConfirmation(item.id, item.code)}><i className="fas fa-trash-alt text-danger"></i> Delete</Dropdown.Item>
                                                                </>}
                                                                {item.place?.eInvoiceEnabled && item.activedOn && !item.isTest && item.price && <>
                                                                    <Dropdown.ItemText className="text-muted">E-INVOICE</Dropdown.ItemText>
                                                                    {!item.invoiceStatus && hasPermission(Permissions.AllVouchers.IssueInvoice) && <>
                                                                        <Dropdown.Item onClick={() => showIssueInvoiceConfirmation(item.id, item.code)}><i className="fas fa-file-invoice text-success"></i> Issue</Dropdown.Item></>
                                                                    }
                                                                    {item.invoiceStatus && hasPermission(Permissions.AllVouchers.ReplaceInvoice) && <>
                                                                        <Dropdown.Item onClick={() => showAdjustOrReplaceInvoiceModal(item.id, item.code)}><i className="fas fa-file-invoice text-warning"></i> Adjust/Replace</Dropdown.Item></>
                                                                    }
                                                                </>}
                                                            </DropdownButton>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedFormModal} ref={formRef as RefObject<HTMLFormElement>}>
                        {formData.id &&
                            <Form.Group className="mb-3" controlId="code">
                                <Form.Label>Code</Form.Label>
                                <Form.Control type="text" readOnly name="code" value={formData.code} />
                            </Form.Group>
                        }
                        {formData.id === null &&
                            <>
                                <VoucherTypeSelect required providerId={state.currentProviderId} placeId={formData.placeId} voucherTypeId={formData.voucherTypeId} onChange={handleFormControlChange}></VoucherTypeSelect>
                                <Form.Group className="mb-3" controlId="quantity">
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control type="number" name="quantity" value={formData.quantity || ""} onChange={(e) => handleFormControlChange(e as any)} />
                                </Form.Group>
                            </>
                        }
                        <Form.Group className="mb-3" controlId="note">
                            <Form.Label>Note</Form.Label>
                            <Form.Control type="text" name="note" value={formData.note} onChange={(e) => handleFormControlChange(e as any)} />
                        </Form.Group>
                        {formData.id === null &&
                            <>
                                <Form.Check type="checkbox" className="text-muted" label="Generate the same keycode and concatenate consecutive numbers (e.g. 12345678-1, 12345678-2, ...)" name="isSameCode" onChange={(e) => handleFormControlChange(e as any)} />
                                <Form.Check type="checkbox" className="text-muted" label="Free" name="paymentService" onChange={(e) => handleFormControlChange(e as any)} />
                            </>
                        }
                        {formData.id &&
                            <Form.Check type="checkbox" className="text-muted" label="Test" name="isTest" id="isTest" onChange={(e) => handleFormControlChange(e as any)} checked={formData.isTest || false} />
                        }
                    </Form>
                    {messageFormModal !== "" && <Alert variant="danger" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}><i className="fas fa-times"></i> Cancel</Button>
                    <Button variant="info" onClick={handleSave}><i className="fas fa-save"></i> Save</Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmation show={displayConfirmationModal} onConfirm={handleDelete} onHide={hideConfirmationModal} id={selectedItem.id} name={selectedItem.name} />

            <HtmlToPrint
                htmlContent={voucherContentToPrint?.htmlContent || ""}
                styleSheet={voucherContentToPrint?.styleSheet || ""}
                paperSizeWidth={voucherContentToPrint?.paperSizeWidth || ""}
                paperSizeHeight={voucherContentToPrint?.paperSizeHeight || ""}
                ref={printRef}
            ></HtmlToPrint>

            <Confirmation show={displayIssueInvoiceConfirmationModal} onConfirm={handleIssueInvoice} onHide={hideIssueInvoiceConfirmationModal} message={`Confirm Issue Invoice #${selectedItem.name}?`} />
            
            <Modal show={displayAdjustOrReplaceInvoiceModal} onHide={hideAdjustOrReplaceInvoiceModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Adjust/Replace Invoice (#{selectedItem.name})</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedAdjustOrReplaceInvoiceInput} ref={invoiceFormRef as RefObject<HTMLFormElement>}>
                        <Form.Group className="mb-3" controlId="referenceType">
                            <Form.Label>Reference Type</Form.Label>
                            <Form.Select name="referenceType" required value={adjustOrReplaceInvoiceInput.referenceType || ""} onChange={(e) => handleAdjustOrReplaceInvoiceFormControlChange(e as any)}>
                                <option value=""></option>
                                <option value="1">Thay thế</option>
                                <option value="2">Điều chỉnh</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">Select a Reference Type.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="orgInvoiceType">
                            <Form.Label>Organization Invoice Type</Form.Label>
                            <Form.Select name="orgInvoiceType" required value={adjustOrReplaceInvoiceInput.orgInvoiceType || ""} onChange={(e) => handleAdjustOrReplaceInvoiceFormControlChange(e as any)}>
                                <option value=""></option>
                                <option value="1">Hóa đơn NĐ 123</option>
                                <option value="3">Hóa đơn NĐ 51</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">Select a Organization Invoice Type.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="invoiceNote">
                            <Form.Label>Reason for replacement/adjustment</Form.Label>
                            <Form.Control type="text" name="invoiceNote" required value={adjustOrReplaceInvoiceInput.invoiceNote} onChange={(e) => handleAdjustOrReplaceInvoiceFormControlChange(e as any)} />
                            <Form.Control.Feedback type="invalid">Please provide the reason for replacement or adjustment.</Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={hideAdjustOrReplaceInvoiceModal}><i className="fas fa-times"></i> Cancel</Button>
                    <Button variant="info" onClick={handleAdjustOrReplaceInvoice}><i className="fas fa-save"></i> Adjust/Replace</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showIssueInvoicesModal} onHide={hideIssueInvoicesModal} backdrop="static" keyboard={false} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Issue Invoices</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedIssueInvoicesFormModal} ref={issueInvoicesFormRef as RefObject<HTMLFormElement>} className="form-inline">
                        <PlaceSelect required providerId={state.currentProviderId} placeId={issueInvoicesInput.placeId} onChange={handleIssueInvoicesFormControlChange}></PlaceSelect>
                        <Form.Group controlId="activedOn" className="mb-3">
                            <Form.Label>Invoice Date</Form.Label>
                            <DatePicker name="activedOn" required={true} value={issueInvoicesInput.activedOn} onChange={handleIssueInvoicesFormControlChange}></DatePicker>
                            <Form.Control.Feedback type="invalid">Select a date.</Form.Control.Feedback>
                        </Form.Group>
                        <Table responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th style={{ width: "10px" }}>
                                        <Form.Check type="checkbox" id="chkAllInvoices" checked={isSelectedAll} onChange={handleCheckAllInvoices} />
                                    </th>
                                    <th>Actived On</th>
                                    <th>Code</th>
                                    <th>Place</th>
                                    <th>Price</th>
                                    <th>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map(item => <tr key={item.id}>
                                    <td>
                                        <Form.Check type="checkbox" id={`chkOneInvoice{item.id}`} checked={item.isSelected == true} value={item.id || "0"} onChange={handleCheckOneInvoice} />
                                    </td>
                                    <td>
                                        {Utils.dateToString(item.activedOn, Configs.DATE_TIME_FORMAT)}
                                        {item.paymentService && <div className="text-muted" style={{ fontSize: "11px" }}>
                                            {item.paymentService || ""}
                                        </div>}
                                        {item.creator?.userName && <div className="text-muted" style={{ fontSize: "11px" }}>
                                            Created by: <span className="text-primary">{item.creator?.userName}</span>
                                        </div>}
                                    </td>
                                    <td>
                                        <h6>{item.code}</h6>
                                    </td>
                                    <td>{item.place?.name || ""}</td>
                                    <td>
                                        {item.price && <span className="text-danger">{Utils.formatNumber(item.price)} {item.currency}</span>}/{(item.timeLimit || 0) > 0 ? <span className="text-muted">{item.timeLimit} hours</span> : <span className="text-muted">Unlimited</span>}
                                        {item.voucherType?.name && <div className="text-muted">
                                            ({item.voucherType?.name || ""})
                                        </div>}
                                    </td>
                                    <td style={{ whiteSpace: "pre-wrap" }}>{item.note}</td>
                                </tr>)}
                            </tbody>
                        </Table>
                    </Form>
                    {messageFormModal !== "" && <Alert variant="info" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={hideIssueInvoicesModal}><i className="fas fa-times"></i> Cancel</Button>
                    <Button variant="info" onClick={handleIssueInvoicesByDate}>
                        <i className="fas fa-file-invoice"></i> Issue Invoices
                    </Button>
                </Modal.Footer>
            </Modal>

            {processing && <div className="processing"><Spinner animation="border" className="mr-2" /> Processing...</div>}
        </>
    )
}

export default Vouchers